import { z } from 'zod';

/** Vertical dashboard zones (Arbeit / IT-Lagebild). */
export const DASHBOARD_ZONE_IDS = ['arbeit', 'lagebild'] as const;
export const dashboardZoneIdSchema = z.enum(DASHBOARD_ZONE_IDS);
export type DashboardZoneId = z.infer<typeof dashboardZoneIdSchema>;

/** Alias used in the modular-dashboard proposal. */
export type DashboardZone = DashboardZoneId;

/** Domain Lagebild panel slot IDs (existing `data-dashboard-slot` values). */
export const DASHBOARD_LAGEBILD_WIDGET_IDS = [
  'security',
  'assets-licenses',
  'helpdesk',
  'top-problems',
  'network',
  'server-storage',
] as const;

/** Arbeit-zone widget IDs. */
export const DASHBOARD_ARBEIT_WIDGET_IDS = [
  'work-major-incidents',
  'work-mine',
  'work-unassigned',
  'work-sla-risk',
  'work-waiting',
  'work-upcoming-changes',
] as const;

export const DASHBOARD_WIDGET_IDS = [
  ...DASHBOARD_ARBEIT_WIDGET_IDS,
  ...DASHBOARD_LAGEBILD_WIDGET_IDS,
] as const;

export const dashboardWidgetIdSchema = z.enum(DASHBOARD_WIDGET_IDS);
export type DashboardWidgetId = z.infer<typeof dashboardWidgetIdSchema>;

/** Compatible with existing Domänen-panel `slotId` props. */
export type DashboardPanelSlotId = (typeof DASHBOARD_LAGEBILD_WIDGET_IDS)[number];

export const DASHBOARD_WIDGETS_BY_ZONE: Record<DashboardZoneId, readonly DashboardWidgetId[]> = {
  arbeit: DASHBOARD_ARBEIT_WIDGET_IDS,
  lagebild: DASHBOARD_LAGEBILD_WIDGET_IDS,
};

export const DEFAULT_DASHBOARD_ZONE_ORDER: DashboardZoneId[] = ['arbeit', 'lagebild'];

export type DashboardZonePrefs = {
  order: DashboardWidgetId[];
  hidden: DashboardWidgetId[];
};

export type DashboardLayoutPrefs = {
  version: 1;
  zoneOrder: DashboardZoneId[];
  zoneHidden: DashboardZoneId[];
  zones: {
    arbeit: DashboardZonePrefs;
    lagebild: DashboardZonePrefs;
  };
};

const dashboardZonePrefsSchema = z.object({
  order: z.array(dashboardWidgetIdSchema),
  hidden: z.array(dashboardWidgetIdSchema),
});

/** Strict schema for a fully normalized layout. */
export const dashboardLayoutPrefsSchema = z.object({
  version: z.literal(1),
  zoneOrder: z.array(dashboardZoneIdSchema).min(1),
  zoneHidden: z.array(dashboardZoneIdSchema),
  zones: z.object({
    arbeit: dashboardZonePrefsSchema,
    lagebild: dashboardZonePrefsSchema,
  }),
});

/** Lenient input — unknown IDs and missing fields are repaired by merge. */
const dashboardLayoutPrefsInputSchema = z
  .object({
    version: z.literal(1).optional(),
    zoneOrder: z.array(z.string()).optional(),
    zoneHidden: z.array(z.string()).optional(),
    zones: z
      .object({
        arbeit: z
          .object({
            order: z.array(z.string()).optional(),
            hidden: z.array(z.string()).optional(),
          })
          .optional(),
        lagebild: z
          .object({
            order: z.array(z.string()).optional(),
            hidden: z.array(z.string()).optional(),
          })
          .optional(),
      })
      .optional(),
  })
  .passthrough();

export function dashboardLayoutStorageKey(userId: string): string {
  return `gb-ticket.dashboard-layout.v1.${userId}`;
}

function uniquePreserveOrder<T>(items: readonly T[]): T[] {
  const seen = new Set<T>();
  const out: T[] = [];
  for (const item of items) {
    if (seen.has(item)) {
      continue;
    }
    seen.add(item);
    out.push(item);
  }
  return out;
}

function isZoneId(value: string): value is DashboardZoneId {
  return (DASHBOARD_ZONE_IDS as readonly string[]).includes(value);
}

function isWidgetId(value: string): value is DashboardWidgetId {
  return (DASHBOARD_WIDGET_IDS as readonly string[]).includes(value);
}

function widgetsForZone(zone: DashboardZoneId): readonly DashboardWidgetId[] {
  return DASHBOARD_WIDGETS_BY_ZONE[zone];
}

function defaultZonePrefs(zone: DashboardZoneId): DashboardZonePrefs {
  const catalog = widgetsForZone(zone);
  return {
    order: [...catalog],
    hidden: [],
  };
}

/** Fresh layout matching today's AgentWorkDashboard / DomainGrid order. */
export function createDefaultDashboardLayout(): DashboardLayoutPrefs {
  return {
    version: 1,
    zoneOrder: [...DEFAULT_DASHBOARD_ZONE_ORDER],
    zoneHidden: [],
    zones: {
      arbeit: defaultZonePrefs('arbeit'),
      lagebild: defaultZonePrefs('lagebild'),
    },
  };
}

function mergeZoneOrder(stored: readonly string[] | undefined): DashboardZoneId[] {
  const fromStored = uniquePreserveOrder((stored ?? []).filter(isZoneId));
  const missing = DEFAULT_DASHBOARD_ZONE_ORDER.filter((id) => !fromStored.includes(id));
  return [...fromStored, ...missing];
}

function mergeZoneHidden(stored: readonly string[] | undefined): DashboardZoneId[] {
  return uniquePreserveOrder((stored ?? []).filter(isZoneId));
}

/**
 * Merge stored zone widget prefs with the current catalog.
 * - Keeps known order / hidden entries
 * - New catalog widgets with defaultEnabled=true → append to order
 * - New catalog widgets with defaultEnabled=false → append to hidden
 * - order ∩ hidden → prefer hidden (drop from order)
 */
export function mergeZonePrefs(
  zone: DashboardZoneId,
  stored: { order?: readonly string[]; hidden?: readonly string[] } | undefined,
  defaultEnabled: Readonly<Record<DashboardWidgetId, boolean>>,
): DashboardZonePrefs {
  const catalog = widgetsForZone(zone);
  const catalogSet = new Set<DashboardWidgetId>(catalog);

  const hidden = uniquePreserveOrder(
    (stored?.hidden ?? []).filter(
      (id): id is DashboardWidgetId => isWidgetId(id) && catalogSet.has(id),
    ),
  );
  const hiddenSet = new Set(hidden);

  const order = uniquePreserveOrder(
    (stored?.order ?? [])
      .filter((id): id is DashboardWidgetId => isWidgetId(id) && catalogSet.has(id))
      .filter((id) => !hiddenSet.has(id)),
  );

  const known = new Set<DashboardWidgetId>([...order, ...hidden]);
  for (const id of catalog) {
    if (known.has(id)) {
      continue;
    }
    if (defaultEnabled[id] === false) {
      hidden.push(id);
    } else {
      order.push(id);
    }
  }

  return { order, hidden };
}

export type DashboardWidgetDefaultEnabled = Readonly<Partial<Record<DashboardWidgetId, boolean>>>;

/**
 * Repair partial / legacy / slightly corrupt prefs into a full DashboardLayoutPrefs.
 * Completely unusable input falls back to defaults (callers should pass objects from JSON).
 */
export function mergeDashboardLayoutPrefs(
  raw: unknown,
  defaultEnabled: DashboardWidgetDefaultEnabled = {},
): DashboardLayoutPrefs {
  const defaults = createDefaultDashboardLayout();
  const enabledMap = Object.fromEntries(
    DASHBOARD_WIDGET_IDS.map((id) => [id, defaultEnabled[id] !== false]),
  ) as Record<DashboardWidgetId, boolean>;

  if (raw === null || raw === undefined || typeof raw !== 'object') {
    return defaults;
  }

  const loose = dashboardLayoutPrefsInputSchema.safeParse(raw);
  if (!loose.success) {
    return defaults;
  }

  const input = loose.data;
  return {
    version: 1,
    zoneOrder: mergeZoneOrder(input.zoneOrder),
    zoneHidden: mergeZoneHidden(input.zoneHidden),
    zones: {
      arbeit: mergeZonePrefs('arbeit', input.zones?.arbeit, enabledMap),
      lagebild: mergeZonePrefs('lagebild', input.zones?.lagebild, enabledMap),
    },
  };
}

/** Parse unknown JSON value; corrupt → default layout. */
export function parseDashboardLayoutPrefs(
  raw: unknown,
  defaultEnabled?: DashboardWidgetDefaultEnabled,
): DashboardLayoutPrefs {
  return mergeDashboardLayoutPrefs(raw, defaultEnabled);
}

export function loadDashboardLayoutFromStorage(
  userId: string,
  defaultEnabled?: DashboardWidgetDefaultEnabled,
): DashboardLayoutPrefs {
  try {
    const raw = window.localStorage.getItem(dashboardLayoutStorageKey(userId));
    if (!raw) {
      return createDefaultDashboardLayout();
    }
    return parseDashboardLayoutPrefs(JSON.parse(raw) as unknown, defaultEnabled);
  } catch {
    return createDefaultDashboardLayout();
  }
}

export function saveDashboardLayoutToStorage(userId: string, layout: DashboardLayoutPrefs): void {
  try {
    const normalized = dashboardLayoutPrefsSchema.parse(mergeDashboardLayoutPrefs(layout));
    window.localStorage.setItem(dashboardLayoutStorageKey(userId), JSON.stringify(normalized));
  } catch {
    // Ignore quota / private-mode failures; in-session state still works.
  }
}

/** Active zones top → bottom (excludes zoneHidden). */
export function visibleDashboardZoneOrder(layout: DashboardLayoutPrefs): DashboardZoneId[] {
  const hidden = new Set(layout.zoneHidden);
  return layout.zoneOrder.filter((id) => !hidden.has(id));
}

/** Visible widget order for a zone (does not apply RBAC — caller filters with registry). */
export function visibleDashboardWidgetOrder(
  layout: DashboardLayoutPrefs,
  zone: DashboardZoneId,
): DashboardWidgetId[] {
  if (layout.zoneHidden.includes(zone)) {
    return [];
  }
  return layout.zones[zone].order;
}

export function hideDashboardZone(
  layout: DashboardLayoutPrefs,
  zoneId: DashboardZoneId,
): DashboardLayoutPrefs {
  if (layout.zoneHidden.includes(zoneId)) {
    return layout;
  }
  return {
    ...layout,
    zoneHidden: [...layout.zoneHidden, zoneId],
  };
}

export function showDashboardZone(
  layout: DashboardLayoutPrefs,
  zoneId: DashboardZoneId,
): DashboardLayoutPrefs {
  if (!layout.zoneHidden.includes(zoneId)) {
    return layout;
  }
  return {
    ...layout,
    zoneHidden: layout.zoneHidden.filter((id) => id !== zoneId),
  };
}

export function hideDashboardWidget(
  layout: DashboardLayoutPrefs,
  zoneId: DashboardZoneId,
  widgetId: DashboardWidgetId,
): DashboardLayoutPrefs {
  const zone = layout.zones[zoneId];
  if (zone.hidden.includes(widgetId)) {
    return layout;
  }
  return {
    ...layout,
    zones: {
      ...layout.zones,
      [zoneId]: {
        order: zone.order.filter((id) => id !== widgetId),
        hidden: [...zone.hidden.filter((id) => id !== widgetId), widgetId],
      },
    },
  };
}

export function showDashboardWidget(
  layout: DashboardLayoutPrefs,
  zoneId: DashboardZoneId,
  widgetId: DashboardWidgetId,
): DashboardLayoutPrefs {
  const zone = layout.zones[zoneId];
  const catalog = widgetsForZone(zoneId);
  if (!catalog.includes(widgetId)) {
    return layout;
  }
  if (zone.order.includes(widgetId) && !zone.hidden.includes(widgetId)) {
    return layout;
  }
  return {
    ...layout,
    zones: {
      ...layout.zones,
      [zoneId]: {
        order: [...zone.order.filter((id) => id !== widgetId), widgetId],
        hidden: zone.hidden.filter((id) => id !== widgetId),
      },
    },
  };
}

/** Swap two zones in `zoneOrder` (full list, including hidden). */
export function reorderDashboardZones(
  layout: DashboardLayoutPrefs,
  activeZoneId: DashboardZoneId,
  overZoneId: DashboardZoneId,
): DashboardLayoutPrefs {
  const order = [...layout.zoneOrder];
  const oldIndex = order.indexOf(activeZoneId);
  const newIndex = order.indexOf(overZoneId);
  if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
    return layout;
  }
  const next = [...order];
  const [removed] = next.splice(oldIndex, 1);
  if (removed === undefined) {
    return layout;
  }
  next.splice(newIndex, 0, removed);
  return { ...layout, zoneOrder: next };
}

/** Move a visible zone up/down among visible zones only. */
export function moveDashboardZone(
  layout: DashboardLayoutPrefs,
  zoneId: DashboardZoneId,
  direction: 'up' | 'down',
): DashboardLayoutPrefs {
  const visible = visibleDashboardZoneOrder(layout);
  const index = visible.indexOf(zoneId);
  const swapWith = direction === 'up' ? visible[index - 1] : visible[index + 1];
  if (index < 0 || !swapWith) {
    return layout;
  }
  const order = [...layout.zoneOrder];
  const a = order.indexOf(zoneId);
  const b = order.indexOf(swapWith);
  if (a < 0 || b < 0) {
    return layout;
  }
  order[a] = swapWith;
  order[b] = zoneId;
  return { ...layout, zoneOrder: order };
}

const PINNED_ARBEIT_WIDGET: DashboardWidgetId = 'work-major-incidents';

function sortableWidgetOrder(
  zoneId: DashboardZoneId,
  order: readonly DashboardWidgetId[],
): DashboardWidgetId[] {
  if (zoneId === 'arbeit') {
    return order.filter((id) => id !== PINNED_ARBEIT_WIDGET);
  }
  return [...order];
}

function withPinnedArbeitBanner(
  zoneId: DashboardZoneId,
  previousOrder: readonly DashboardWidgetId[],
  nextCards: readonly DashboardWidgetId[],
): DashboardWidgetId[] {
  if (zoneId === 'arbeit' && previousOrder.includes(PINNED_ARBEIT_WIDGET)) {
    return [PINNED_ARBEIT_WIDGET, ...nextCards];
  }
  return [...nextCards];
}

/** Reorder widgets within a zone. Banner stays pinned at top of Arbeit. */
export function reorderDashboardWidgets(
  layout: DashboardLayoutPrefs,
  zoneId: DashboardZoneId,
  activeWidgetId: DashboardWidgetId,
  overWidgetId: DashboardWidgetId,
): DashboardLayoutPrefs {
  if (activeWidgetId === PINNED_ARBEIT_WIDGET || overWidgetId === PINNED_ARBEIT_WIDGET) {
    return layout;
  }
  const zone = layout.zones[zoneId];
  const cards = sortableWidgetOrder(zoneId, zone.order);
  const oldIndex = cards.indexOf(activeWidgetId);
  const newIndex = cards.indexOf(overWidgetId);
  if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
    return layout;
  }
  const nextCards = [...cards];
  const [removed] = nextCards.splice(oldIndex, 1);
  if (removed === undefined) {
    return layout;
  }
  nextCards.splice(newIndex, 0, removed);
  return {
    ...layout,
    zones: {
      ...layout.zones,
      [zoneId]: {
        ...zone,
        order: withPinnedArbeitBanner(zoneId, zone.order, nextCards),
      },
    },
  };
}

export function moveDashboardWidget(
  layout: DashboardLayoutPrefs,
  zoneId: DashboardZoneId,
  widgetId: DashboardWidgetId,
  direction: 'up' | 'down',
): DashboardLayoutPrefs {
  if (widgetId === PINNED_ARBEIT_WIDGET) {
    return layout;
  }
  const cards = sortableWidgetOrder(zoneId, layout.zones[zoneId].order);
  const index = cards.indexOf(widgetId);
  const swapWith = direction === 'up' ? cards[index - 1] : cards[index + 1];
  if (index < 0 || !swapWith) {
    return layout;
  }
  return reorderDashboardWidgets(layout, zoneId, widgetId, swapWith);
}
